import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { adminApi, mediaUrl, errMsg } from "../lib";

export const STATUSES = ["pending", "approved", "private", "rejected", "removed"];

export function useAdmin(path) {
  const [data, setData] = useState(null);
  const load = useCallback(() => adminApi.get(path).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [path]);
  useEffect(() => { load(); }, [load]);
  return [data, load, setData];
}

export const save = async (fn, msg = "Saved") => {
  try { const r = await fn(); toast.success(msg); return r?.data; }
  catch (e) { toast.error(errMsg(e)); return null; }
};

export const Panel = ({ title, children, actions, testid }) => (
  <section className="bg-white border border-lou-line p-5 sm:p-7 mb-8" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <h2 className="font-lou text-2xl">{title}</h2>
      {actions}
    </div>
    {children}
  </section>
);

export const In = ({ label, value, onChange, textarea, rows = 4, testid, ...p }) => (
  <label className="block">
    <span className="lou-label">{label}</span>
    {textarea ? <textarea className="lou-input text-sm" rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...p} />
      : <input className="lou-input text-sm !py-2" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...p} />}
  </label>
);

export const Check = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#4A4592] w-4 h-4" data-testid={testid} />{label}
  </label>
);

export const StatusSelect = ({ value, onChange, testid, options = STATUSES }) => (
  <select className="lou-input !py-1.5 !w-auto text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {options.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

async function uploadLarge(file, onProgress) {
  const { data: init } = await adminApi.post("/upload/init", { filename: file.name, content_type: file.type, size: file.size });
  const n = Math.ceil(file.size / init.chunk_size);
  for (let i = 0; i < n; i++) {
    const fd = new FormData();
    fd.append("index", i);
    fd.append("chunk", file.slice(i * init.chunk_size, (i + 1) * init.chunk_size), `part-${i}`);
    await adminApi.post(`/upload/${init.upload_id}/chunk`, fd);
    onProgress(Math.round(((i + 1) / n) * 100));
  }
  return (await adminApi.post(`/upload/${init.upload_id}/complete`)).data;
}

export function Uploader({ onUploaded, accept = "image/jpeg,image/png,image/webp", multiple, label = "Upload", testid }) {
  const [busy, setBusy] = useState(null);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const out = [];
    for (const f of files) {
      try {
        setBusy(`${f.name} 0%`);
        const isLarge = f.type.startsWith("video/") || f.type.startsWith("audio/");
        const m = isLarge ? await uploadLarge(f, (p) => setBusy(`${f.name} ${p}%`))
          : (await adminApi.post("/upload", (() => { const fd = new FormData(); fd.append("file", f); return fd; })())).data;
        out.push(m);
      } catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    setBusy(null);
    if (out.length) { onUploaded(out); toast.success(`${out.length} file(s) uploaded — originals kept privately`); }
  };
  return (
    <label className={`lou-btn-ghost text-sm cursor-pointer ${busy ? "opacity-70" : ""}`} data-testid={testid}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy || label}
      <input type="file" className="sr-only" accept={accept} multiple={multiple} onChange={pick} disabled={!!busy} data-testid={testid ? `${testid}-input` : undefined} />
    </label>
  );
}

export const Thumbs = ({ ids = [], onRemove, sample }) => (
  <div className="flex flex-wrap gap-2">
    {ids.map((id) => (
      <div key={id} className="relative">
        <img src={mediaUrl(id, true)} alt="" className="h-20 w-auto border border-lou-line" />
        {onRemove && <button type="button" onClick={() => onRemove(id)} aria-label="Remove image" className="absolute -top-2 -right-2 bg-lou-ink text-white rounded-full p-0.5"><X size={12} /></button>}
      </div>
    ))}
    {!ids.length && sample && <img src={sample} alt="" className="h-20 w-auto opacity-70 border border-dashed border-lou-ochre" />}
  </div>
);

export const SampleBadge = ({ show }) => show ? <span className="text-[10px] uppercase tracking-widest bg-lou-ochre text-white px-1.5 py-0.5 ml-2">sample</span> : null;
